import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { getStaffVerificacion } from '../../services/staff-lector';

const useStyles = makeStyles(theme => ({
    centeredState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 180,
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: theme.palette.text.section,
        marginBottom: theme.spacing(1.5),
        marginTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.border.section}`,
        '&:first-child': { marginTop: 0 },
    },
    summaryCard: {
        height: '100%',
        padding: theme.spacing(2),
        borderRadius: 5,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.border.soft}`,
    },
    summaryLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    summaryValue: {
        fontSize: 26,
        fontWeight: 700,
        color: theme.palette.primary.main,
        lineHeight: 1.1,
    },
    summaryCaption: {
        fontSize: 12,
        color: theme.palette.text.disabled,
        marginTop: theme.spacing(0.5),
    },
    listContainer: {
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    tableHead: {
        backgroundColor: theme.palette.surface.panel,
    },
    headCell: {
        color: theme.palette.text.section,
        fontWeight: 700,
        borderBottom: `2px solid ${theme.palette.border.main}`,
        whiteSpace: 'nowrap',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    bodyRow: {
        '&:nth-child(even)': {
            backgroundColor: theme.palette.surface.soft,
        },
    },
    bodyCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    clientLink: {
        fontWeight: 700,
        color: theme.palette.primary.main,
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
    },
    pendingValue: {
        fontWeight: 700,
        color: theme.palette.warning.main,
    },
    progressBar: {
        height: 6,
        borderRadius: 3,
        backgroundColor: theme.palette.border.soft,
        overflow: 'hidden',
        flex: 1,
        minWidth: 60,
    },
    progressFill: {
        height: '100%',
        borderRadius: 3,
        backgroundColor: theme.palette.primary.main,
    },
    backButton: {
        borderRadius: 5,
        textTransform: 'none',
        fontWeight: 700,
        border: `1px solid ${theme.palette.border.button}`,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.surface.alt,
        boxShadow: theme.palette.shadow.card,
        marginBottom: theme.spacing(2.5),
        '&:hover': { backgroundColor: theme.palette.surface.neutral },
    },
}));

function MetricCard({ label, value, caption }) {
    const classes = useStyles();
    return (
        <Box className={classes.summaryCard}>
            <Typography className={classes.summaryLabel}>{label}</Typography>
            <Typography className={classes.summaryValue}>{value ?? '—'}</Typography>
            {caption && <Typography className={classes.summaryCaption}>{caption}</Typography>}
        </Box>
    );
}

function ProgressCell({ pct }) {
    const classes = useStyles();
    return (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Box className={classes.progressBar}>
                <Box className={classes.progressFill} style={{ width: `${Math.min(pct ?? 0, 100)}%` }} />
            </Box>
            <Typography variant="body2" style={{ whiteSpace: 'nowrap', minWidth: 44, textAlign: 'right', color: 'inherit' }}>
                {pct != null ? `${pct.toFixed(1)}%` : '—'}
            </Typography>
        </Box>
    );
}

function fmt(n) {
    if (n == null) return '—';
    return Number(n).toLocaleString('es-AR');
}

function GlobalStats({ g }) {
    return (
        <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
                <MetricCard label="Total facturas" value={fmt(g?.total)} />
            </Grid>
            <Grid item xs={6} sm={3}>
                <MetricCard label="Verificadas" value={fmt(g?.revisadas)} />
            </Grid>
            <Grid item xs={6} sm={3}>
                <MetricCard label="Pendientes" value={fmt(g?.pendientes)} />
            </Grid>
            <Grid item xs={6} sm={3}>
                <MetricCard label="% Verificadas" value={g?.pct_verificadas != null ? `${g.pct_verificadas.toFixed(1)}%` : '—'} />
            </Grid>
        </Grid>
    );
}

function ClienteDetail({ clienteId, onBack }) {
    const classes = useStyles();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getStaffVerificacion({ cliente_id: clienteId })
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [clienteId]);

    if (loading) return <Box className={classes.centeredState}><CircularProgress size={28} /></Box>;
    if (!data) return <Typography color="error">No se pudo cargar el detalle.</Typography>;

    return (
        <Box>
            <Button className={classes.backButton} startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
                Volver a todos los clientes
            </Button>

            <Typography className={classes.sectionLabel}>Resumen del cliente</Typography>
            <GlobalStats g={data.global} />

            <Typography className={classes.sectionLabel}>Por localización</Typography>
            <TableContainer className={classes.listContainer}>
                <Table size="small">
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.headCell}>Localización</TableCell>
                            <TableCell className={classes.headCell} align="right">Total</TableCell>
                            <TableCell className={classes.headCell} align="right">Verificadas</TableCell>
                            <TableCell className={classes.headCell} align="right">Pendientes</TableCell>
                            <TableCell className={classes.headCell} style={{ minWidth: 180 }}>% Verificadas</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(data.por_localizacion || []).map(loc => (
                            <TableRow key={loc.jerarquia_id ?? 'huerfana'} className={classes.bodyRow}>
                                <TableCell className={classes.bodyCell}>
                                    {loc.es_huerfana
                                        ? <em style={{ color: '#9ca3af' }}>Sin localización</em>
                                        : loc.jerarquia_nombre || `ID ${loc.jerarquia_id}`
                                    }
                                </TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(loc.total)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(loc.revisadas)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">
                                    {loc.pendientes > 0
                                        ? <span className={classes.pendingValue}>{fmt(loc.pendientes)}</span>
                                        : fmt(loc.pendientes)
                                    }
                                </TableCell>
                                <TableCell className={classes.bodyCell}>
                                    <ProgressCell pct={loc.pct_verificadas} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {!data.por_localizacion?.length && <EmptyRow cols={5} />}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default function VerificacionTab() {
    const classes = useStyles();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedCliente, setSelectedCliente] = useState(null);

    const fetchGlobal = useCallback(() => {
        setLoading(true);
        getStaffVerificacion()
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchGlobal(); }, [fetchGlobal]);

    if (selectedCliente) {
        return <ClienteDetail clienteId={selectedCliente} onBack={() => setSelectedCliente(null)} />;
    }

    if (loading) return <Box className={classes.centeredState}><CircularProgress size={32} /></Box>;
    if (!data) return <Typography color="error">No se pudo cargar la verificación.</Typography>;

    return (
        <Box>
            <Typography className={classes.sectionLabel}>Resumen global</Typography>
            <GlobalStats g={data.global} />

            <Typography className={classes.sectionLabel}>Por cliente — más pendientes primero</Typography>
            <TableContainer className={classes.listContainer}>
                <Table size="small">
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.headCell}>Cliente</TableCell>
                            <TableCell className={classes.headCell} align="right">Total</TableCell>
                            <TableCell className={classes.headCell} align="right">Verificadas</TableCell>
                            <TableCell className={classes.headCell} align="right">Pendientes</TableCell>
                            <TableCell className={classes.headCell} style={{ minWidth: 200 }}>% Verificadas</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(data.por_cliente || []).map(c => (
                            <TableRow key={c.cliente_id} className={classes.bodyRow}>
                                <TableCell className={classes.bodyCell}>
                                    <Tooltip title="Ver por localización" arrow>
                                        <span className={classes.clientLink} onClick={() => setSelectedCliente(c.cliente_id)}>
                                            {c.email}
                                        </span>
                                    </Tooltip>
                                    {c.razon_social && (
                                        <Typography variant="caption" display="block" color="textSecondary">
                                            {c.razon_social}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(c.total)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(c.revisadas)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">
                                    {c.pendientes > 0
                                        ? <span className={classes.pendingValue}>{fmt(c.pendientes)}</span>
                                        : fmt(c.pendientes)
                                    }
                                </TableCell>
                                <TableCell className={classes.bodyCell}>
                                    <ProgressCell pct={c.pct_verificadas} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {!data.por_cliente?.length && <EmptyRow cols={5} />}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function EmptyRow({ cols }) {
    return (
        <TableRow>
            <TableCell colSpan={cols} align="center" style={{ padding: 24 }}>
                <Typography variant="body2" color="textSecondary">Sin datos</Typography>
            </TableCell>
        </TableRow>
    );
}
